export type CustomerProfileSource = {
  customer_id?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  created_at?: string | null;
};

export type CustomerProfile = {
  id: string;
  customer_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  created_at: string;
};

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase() ?? '';
const normalizePhone = (phone?: string | null) => phone?.replace(/\D/g, '') ?? '';
const normalizeText = (value?: string | null) => value?.trim().toLowerCase() ?? '';
const fallbackDate = () => new Date(0).toISOString();

type ContactIdentity = Pick<CustomerProfileSource, 'customer_id' | 'email' | 'phone'>;
type ContactSnapshot = ContactIdentity & Pick<CustomerProfileSource, 'name' | 'address'>;

function contactKeys(source: ContactIdentity): string[] {
  const keys: string[] = [];
  if (source.customer_id) keys.push(`id:${source.customer_id}`);
  const email = normalizeEmail(source.email);
  if (email) keys.push(`email:${email}`);
  const phone = normalizePhone(source.phone);
  if (phone) keys.push(`phone:${phone}`);
  return keys;
}

function hasContactIdentity(source: ContactIdentity): boolean {
  return contactKeys(source).length > 0;
}

/**
 * Key for snapshots that carry no contact identity (e.g. manually-added job orders with only a
 * client name/address). Lets them contribute a profile while still merging identical entries.
 */
function snapshotKey(source: ContactSnapshot): string | null {
  const name = normalizeText(source.name);
  const address = normalizeText(source.address);
  if (!name && !address) return null;
  return `contact:${name}|${address}`;
}

function profileKeys(source: ContactSnapshot): string[] {
  const keys = contactKeys(source);
  if (keys.length > 0) return keys;
  const fallback = snapshotKey(source);
  return fallback ? [fallback] : [];
}

export function contactKey(source: ContactIdentity): string | null {
  return contactKeys(source)[0] ?? null;
}

export function customerMatchesSource(
  customer: (Pick<CustomerProfile, 'id' | 'customer_id' | 'email' | 'phone'> & Partial<Pick<CustomerProfile, 'name' | 'address'>>) | null | undefined,
  source: ContactSnapshot,
): boolean {
  if (!customer) return false;
  if (source.customer_id && (source.customer_id === customer.customer_id || source.customer_id === customer.id)) return true;
  const sourceEmail = normalizeEmail(source.email);
  if (sourceEmail && sourceEmail === normalizeEmail(customer.email)) return true;
  const sourcePhone = normalizePhone(source.phone);
  if (sourcePhone && sourcePhone === normalizePhone(customer.phone)) return true;
  // Fallback: link name/address-only snapshots when neither side carries a contact identity.
  if (hasContactIdentity(customer) || hasContactIdentity(source)) return false;
  const sourceSnapshot = snapshotKey(source);
  return sourceSnapshot !== null && sourceSnapshot === snapshotKey(customer);
}

function mergeIntoProfile(existing: CustomerProfile | undefined, source: CustomerProfileSource, email: string, canonicalKey: string): CustomerProfile {
  const createdAt = source.created_at || existing?.created_at || fallbackDate();
  return {
    id: source.customer_id || existing?.customer_id || existing?.id || `legacy:${email || normalizePhone(source.phone) || canonicalKey}`,
    customer_id: source.customer_id ?? existing?.customer_id ?? null,
    name: source.name?.trim() || existing?.name || email || normalizePhone(source.phone) || 'Customer',
    email: existing?.email || email || '',
    phone: existing?.phone ?? source.phone ?? null,
    address: existing?.address ?? source.address ?? null,
    created_at: existing?.created_at && existing.created_at < createdAt ? existing.created_at : createdAt,
  };
}

export function mergeCustomerProfiles(sources: CustomerProfileSource[]): CustomerProfile[] {
  const profiles = new Map<string, CustomerProfile>();
  const aliases = new Map<string, string>();

  for (const source of sources) {
    const email = normalizeEmail(source.email);
    const keys = profileKeys(source);
    if (keys.length === 0) continue;

    const canonicalKey = keys.map((key) => aliases.get(key) ?? key).find((key) => profiles.has(key)) ?? keys[0];
    const existing = profiles.get(canonicalKey);
    const merged = mergeIntoProfile(existing, source, email, canonicalKey);

    profiles.set(canonicalKey, merged);
    for (const key of keys) aliases.set(key, canonicalKey);
  }

  return [...profiles.values()].sort((a, b) => b.created_at.localeCompare(a.created_at));
}
